import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Card,
  Avatar,
  Rating,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where, deleteDoc, addDoc, limit, startAfter } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Global, css } from '@emotion/react';

// Mock data and helper functions
const categories = [
  'Electricistas',
  'Plomeros',
  'Carpinteros',
  'Pintores',
  'Jardineros'
];

const locations = [
  'Ciudad de México',
  'Guadalajara',
  'Monterrey',
  'Puebla',
  'Querétaro',
  'Tijuana',
  'Mérida',
  'Cancún',
  'Puerto Vallarta',
  'Acapulco'
];

const names = [
  'Juan Pérez',
  'María García',
  'Carlos López',
  'Ana Martínez',
  'Pedro Sánchez',
  'Laura Rodríguez',
  'Miguel Torres',
  'Sofia Castro',
  'Roberto Díaz',
  'Carmen Ruiz',
  'José González',
  'Isabel Morales',
  'Fernando Silva',
  'Patricia Vargas',
  'Ricardo Mendoza',
  'Lucía Herrera',
  'Alejandro Flores',
  'Elena Ramírez',
  'Daniel Ortiz',
  'Adriana Luna'
];

const descriptions = [
  'Profesional con más de 5 años de experiencia en el campo.',
  'Especialista certificado con amplio conocimiento en el área.',
  'Técnico altamente calificado con excelente reputación.',
  'Experto en servicios residenciales y comerciales.',
  'Profesional dedicado con atención al detalle.',
  'Técnico especializado con experiencia en proyectos grandes.',
  'Profesional confiable con excelentes referencias.',
  'Especialista en soluciones rápidas y efectivas.',
  'Técnico certificado con amplia experiencia.',
  'Profesional comprometido con la calidad del servicio.'
];

const generateRandomRating = () => Math.floor(Math.random() * 2) + 4;
const generateRandomReviewCount = () => Math.floor(Math.random() * 50) + 10;
const generateRandomHourlyRate = () => Math.floor(Math.random() * 200) + 100;
const generateRandomAvatar = (seed) => `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`;

const generateMockProviders = (count) => {
  const providers = [];
  for (let i = 0; i < count; i++) {
    const name = names[Math.floor(Math.random() * names.length)];
    const seed = name.replace(/\s+/g, '').toLowerCase();
    providers.push({
      name,
      category: categories[Math.floor(Math.random() * categories.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      description: descriptions[Math.floor(Math.random() * descriptions.length)],
      rating: generateRandomRating(),
      reviewCount: generateRandomReviewCount(),
      hourlyRate: generateRandomHourlyRate(),
      image: generateRandomAvatar(seed),
      isTopRated: Math.random() > 0.7,
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }
  return providers;
};

const serviceCategories = categories;

const ITEMS_PER_PAGE = 9;

const providerConverter = {
  toFirestore: (provider) => {
    return {
      ...provider,
      createdAt: provider.createdAt || new Date(),
      normalizedName: provider.name.toLowerCase().trim()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    };
  },
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options);
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date()
    };
  }
};

const Home = () => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allProviders, setAllProviders] = useState([]);
  const navigate = useNavigate();

  // Add intersection observer ref for infinite scrolling
  const observerRef = useRef();

  // Add ref for the search input
  const searchInputRef = useRef(null);

  // Define loadMoreProviders function
  const loadMoreProviders = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    console.log('Loading more providers, page:', page + 1);
    setIsLoading(true);
    
    const itemsPerPage = 9;
    const nextPage = page + 1;
    const startIndex = (nextPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    // Get next batch from already filtered results
    const nextItems = allProviders.slice(startIndex, endIndex);
    
    console.log(`Adding ${nextItems.length} more items, total will be: ${providers.length + nextItems.length}/${allProviders.length}`);
    
    if (nextItems.length === 0) {
      console.log('No more items to load');
      setHasMore(false);
      setIsLoading(false);
      return;
    }
    
    // Update state with next batch
    setProviders(prev => [...prev, ...nextItems]);
    setFilteredProviders(prev => [...prev, ...nextItems]);
    setPage(nextPage);
    
    // Only set hasMore to true if there are more items to load after this batch
    const hasMoreItems = endIndex < allProviders.length;
    console.log(`Has more items: ${hasMoreItems}, current end index: ${endIndex}, total items: ${allProviders.length}`);
    setHasMore(hasMoreItems);
    setIsLoading(false);
  }, [page, allProviders, hasMore, isLoading, providers.length]);

  const lastProviderRef = useCallback(node => {
    if (isLoading) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        console.log('Intersection observed, loading more providers...');
        loadMoreProviders();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore, loadMoreProviders]);

  const fetchProviders = async () => {
    try {
      if (isLoading) return;
      
      console.log('Fetching providers:', {
        selectedCategory,
        searchTerm
      });
      
      setIsLoading(true);
      setPage(1);
      
      // Always fetch all providers for more flexible filtering
      const queryRef = collection(db, 'providers').withConverter(providerConverter);
      const allProvidersQuery = query(queryRef, orderBy('createdAt', 'desc'));
      
      const snapshot = await getDocs(allProvidersQuery);
      console.log('Query results before filtering:', {
        empty: snapshot.empty,
        size: snapshot.size
      });
      
      if (snapshot.empty) {
        setProviders([]);
        setFilteredProviders([]);
        setAllProviders([]);
        setHasMore(false);
        setIsLoading(false);
        return;
      }
      
      // Get all providers
      const fetchedProviders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters based on search term and category
      let filteredResults = fetchedProviders;
      
      // Filter by category if selected
      if (selectedCategory) {
        console.log('Filtering by category:', selectedCategory);
        filteredResults = filteredResults.filter(provider => 
          provider.category === selectedCategory
        );
        console.log('After category filtering:', filteredResults.length);
      }
      
      // Filter by search term if provided
      if (searchTerm) {
        const normalizedSearchTerm = searchTerm.toLowerCase().trim()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        
        console.log('Filtering by search term:', normalizedSearchTerm);
        
        filteredResults = filteredResults.filter(provider => {
          // Normalize all searchable fields
          const normalizedName = provider.name?.toLowerCase().trim()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") || '';
          const normalizedCategory = provider.category?.toLowerCase().trim()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") || '';
          const normalizedLocation = provider.location?.toLowerCase().trim()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") || '';
          const normalizedDescription = provider.description?.toLowerCase().trim()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") || '';
          
          // Match against any of the fields
          return normalizedName.includes(normalizedSearchTerm) || 
                 normalizedCategory.includes(normalizedSearchTerm) || 
                 normalizedLocation.includes(normalizedSearchTerm) ||
                 normalizedDescription.includes(normalizedSearchTerm);
        });
        
        console.log('After search term filtering:', filteredResults.length);
      }
      
      console.log('Final filtering results:', {
        allCount: fetchedProviders.length,
        filteredCount: filteredResults.length,
        searchTerm,
        category: selectedCategory
      });
      
      // Store all filtered results for pagination
      setAllProviders(filteredResults);
      
      // Display first page
      const itemsPerPage = 9;
      const firstPageItems = filteredResults.slice(0, itemsPerPage);
      setProviders(firstPageItems);
      setFilteredProviders(firstPageItems);
      setHasMore(filteredResults.length > itemsPerPage);
      
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (event) => {
    if (event.type === 'click' || (event.type === 'keypress' && event.key === 'Enter')) {
      event.preventDefault();
      console.log('Executing search:', { searchTerm });
      // Hide keyboard on mobile by blurring the input
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
      fetchProviders();
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    console.log('Category selection triggered:', {
      currentCategory: selectedCategory,
      newCategory: category,
      timestamp: new Date().toISOString()
    });
    
    const newCategory = category === selectedCategory ? null : category;
    console.log('Setting new category:', newCategory);
    
    // Reset states
    setProviders([]);
    setFilteredProviders([]);
    setSearchTerm('');
    setSelectedCategory(newCategory);
    
    console.log('States reset, fetchProviders will be triggered by useEffect');
  };

  const checkAndPopulateDatabase = useCallback(async () => {
    try {
      console.log('Checking if database needs population...');
      const providersRef = collection(db, 'providers').withConverter(providerConverter);
      const snapshot = await getDocs(providersRef);
      
      if (snapshot.empty) {
        console.log('Database is empty. Populating with mock data...');
        const mockProviders = generateMockProviders(20);
        
        // Add normalized names to mock data
        const normalizedProviders = mockProviders.map(provider => ({
          ...provider,
          normalizedName: provider.name.toLowerCase().trim()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        }));
        
        console.log('Adding providers with normalized data:', normalizedProviders);
        
        for (const provider of normalizedProviders) {
          await addDoc(providersRef, provider);
        }
        console.log('Database populated successfully');
        
        // Fetch providers again to refresh the list
        fetchProviders();
      } else {
        console.log('Database already contains data:', snapshot.size, 'providers');
      }
    } catch (error) {
      console.error('Error checking/populating database:', error);
    }
  }, []);

  useEffect(() => {
    checkAndPopulateDatabase();
  }, [checkAndPopulateDatabase]);

  // Initial load and category changes
  useEffect(() => {
    fetchProviders();
  }, [selectedCategory]);

  return (
    <>
      <Global
        styles={css`
          @keyframes subtleFloat {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
            }
            33% {
              transform: translate(2%, 1%) rotate(1deg);
            }
            66% {
              transform: translate(-1%, -1%) rotate(-1deg);
            }
          }
        `}
      />
      <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
        {/* Hero Section */}
        <Box sx={{ 
          position: 'relative',
          py: { xs: 2, sm: 4, md: 6 },
          bgcolor: 'primary.main',
          overflow: 'hidden',
        }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #FFD900, #FFC107, #FFEA00)',
              opacity: 0.9,
              animation: 'subtleFloat 15s ease-in-out infinite',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 60%)',
                transform: 'rotate(30deg)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-30%',
                right: '-30%',
                width: '150%',
                height: '150%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
                transform: 'rotate(-20deg)',
              },
              zIndex: 0,
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  position: 'relative', 
                  zIndex: 1, 
                  maxWidth: 600,
                  px: { xs: 1, sm: 2, md: 0 } 
                }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.75rem' },
                      fontWeight: 800,
                      color: '#000',
                      mb: { xs: 1.5, md: 3 },
                      fontFamily: 'Ubuntu',
                      lineHeight: { xs: 1.2, md: 1.3 },
                      maxWidth: { xs: '100%', sm: '90%', md: '100%' },
                      wordBreak: 'break-word',
                    }}
                  >
                    Encuentra profesionales calificados para cualquier trabajo que necesites
                  </Typography>

                  {/* Search Box */}
                  <Box
                    sx={{
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: 'white',
                      borderRadius: '50px',
                      p: { xs: '2px', md: '6px' },
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      mb: { xs: 2, sm: 3 },
                      maxWidth: '100%',
                      width: '100%',
                      mx: { xs: 'auto', sm: 0 },
                      overflow: 'hidden',
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Buscar"
                      variant="standard"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyPress={handleSearch}
                      inputRef={searchInputRef}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ ml: { xs: 0.3, md: 2 }, color: 'text.secondary', fontSize: { xs: '0.9rem', md: '1.25rem' } }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end" sx={{ mr: 0 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setSearchTerm('');
                                setSelectedCategory(null);
                                if (searchInputRef.current) {
                                  searchInputRef.current.blur();
                                }
                                setPage(1);
                                setHasMore(true);
                                setTimeout(() => {
                                  fetchProviders();
                                  console.log('Clear button pressed, showing all providers');
                                }, 0);
                              }}
                              aria-label="clear search"
                              sx={{ p: 0.2 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiInputBase-root': {
                          height: { xs: '38px', sm: '42px', md: '48px' },
                          paddingRight: { xs: 0, md: 1 },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', md: '1rem' },
                          px: { xs: 0.2, md: 1 },
                          width: '100%',
                          textOverflow: 'ellipsis',
                          '&::placeholder': {
                            fontSize: { xs: '0.75rem', md: '1rem' },
                            opacity: 0.7,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        },
                      }}
                      autoComplete="off"
                      onFocus={(e) => e.target.select()}
                    />
                    <IconButton
                      onClick={handleSearch}
                      sx={{
                        bgcolor: 'black',
                        color: 'white',
                        '&:hover': { bgcolor: '#333' },
                        mx: { xs: 0.2, md: 1 },
                        width: { xs: '22px', sm: '24px', md: '40px' },
                        height: { xs: '22px', sm: '24px', md: '40px' },
                        minWidth: { xs: '22px', sm: '24px', md: '40px' },
                        p: { xs: 0, md: 'auto' },
                        flexShrink: 0,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '1.5rem' } }} />
                    </IconButton>
                  </Box>
                  
                  {/* Categories */}
                  <Box sx={{ 
                    display: 'flex',
                    gap: { xs: 0.5, sm: 1 },
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    py: { xs: 1, sm: 0 },
                    mb: { xs: 1, sm: 0 },
                    mx: { xs: -1, sm: 0 },
                    px: { xs: 1, sm: 0 },
                    '&::-webkit-scrollbar': {
                      height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      borderRadius: '10px',
                    }
                  }}>
                    {serviceCategories.map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        clickable
                        onClick={() => handleCategorySelect(category)}
                        sx={{
                          bgcolor: selectedCategory === category ? 'black' : 'white',
                          color: selectedCategory === category ? 'white' : 'black',
                          '&:hover': {
                            bgcolor: selectedCategory === category ? '#333' : '#f5f5f5',
                          },
                          fontWeight: 500,
                          py: { xs: 1.5, sm: 2, md: 2.5 },
                          fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                          borderRadius: '50px',
                          flexShrink: 0,
                          '&:active': {
                            transform: 'scale(0.97)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="/repair-hero.jpg"
                  alt="Service Providers"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: 400,
                    objectFit: 'contain',
                    display: { xs: 'none', md: 'block' },
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Providers Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: { xs: 2, sm: 3 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: 'Ubuntu',
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {searchTerm || selectedCategory ? 'Resultados de búsqueda' : 'Proveedores por Calificación'}
            </Typography>
            <Chip 
              label={`${providers.length} encontrados`} 
              sx={{ 
                bgcolor: 'white',
                border: '1px solid #E0E0E0',
                fontFamily: 'Ubuntu',
                fontWeight: 500,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                height: { xs: '24px', sm: '32px' },
                mt: { xs: 0, sm: 0 },
              }} 
            />
          </Box>

          {isLoading && providers.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : providers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron proveedores que coincidan con tu búsqueda
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {providers.map((provider, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={provider.id}
                  ref={index === providers.length - 1 && hasMore ? lastProviderRef : null}
                >
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        transition: 'transform 0.2s',
                        boxShadow: 3
                      },
                      position: 'relative',
                      overflow: 'visible',
                      borderRadius: { xs: '12px', sm: '16px' },
                    }}
                    onClick={() => navigate(`/providers/${provider.id}`)}
                  >
                    {(provider.isTopRated || provider.rating >= 4.5) && (
                      <Chip
                        label="Top Rated"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: 10,
                          bgcolor: '#000',
                          color: '#fff',
                          zIndex: 1,
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                    <Box sx={{ 
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: '#FFE500',
                      borderRadius: { xs: '12px 12px 0 0', sm: '16px 16px 0 0' },
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: '150%',
                        height: '150%',
                        background: 'radial-gradient(circle at 30% 30%, rgba(255,245,150,0.9) 0%, rgba(255,229,0,0.3) 40%, rgba(255,229,0,0) 70%)',
                        top: '-25%',
                        left: '-25%',
                        animation: 'subtleFloat 4s infinite ease-in-out',
                      }
                    }}>
                      <Avatar
                        src={provider.image}
                        alt={provider.name}
                        sx={{ 
                          width: { xs: 60, sm: 70, md: 80 },
                          height: { xs: 60, sm: 70, md: 80 },
                          border: '4px solid #fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          position: 'relative',
                          zIndex: 1
                        }}
                      />
                    </Box>
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }
                        }}
                      >
                        {provider.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Rating 
                          value={provider.rating} 
                          readOnly 
                          size="small" 
                          sx={{ 
                            mr: 1,
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}
                        >
                          ({provider.reviewCount} reviews)
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}
                      >
                        {provider.category} • {provider.location}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          height: { xs: '32px', sm: '36px', md: '40px' },
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' }
                        }}
                      >
                        {provider.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}
                        >
                          Desde ${provider.hourlyRate}/hr
                        </Typography>
                        <Chip 
                          label="Ver perfil" 
                          size="small"
                          sx={{ 
                            bgcolor: '#FFE500',
                            '&:hover': {
                              bgcolor: '#FFD700'
                            },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: '24px', sm: '32px' },
                          }}
                        />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          {isLoading && providers.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          )}
          {!isLoading && !hasMore && providers.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No hay más resultados para mostrar
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Home; 